import { defineDocument } from '../../schema';
export const blogAirheadGamesOfficiallyLaunchesDocument = defineDocument({
    id: 'blog:airhead-games-officially-launches',
    kind: 'blog',
    slug: 'airhead-games-officially-launches',
    route: '/blog/airhead-games-officially-launches',
    seo: {
        title: 'Airhead Games Officially Launches: A New Era for Community Party Games',
        description: 'Celebrating our move to General Availability with the release of Meme Mash',
        imageAsset: 'logoPoppyYT2x',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Airhead Blog',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Airhead Games Officially Launches: A New Era for Community Party Games',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Celebrating our move to General Availability with the release of Meme Mash',
            },
        ],
        imageAsset: 'logoPoppyYT2x',
        imageAlt: 'Airhead Games General Availability Launch',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2025-09-23T00:00:00.000Z'),
        tags: ['news', 'features', 'games'],
    },
    sections: [
        {
            id: 'article',
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The "party game" genre has a problem. We all know the feeling: you buy a game, you have a blast the first two times you play it, and then… you start seeing the same prompts. You memorize the cards. The surprise fades, and the game collects digital dust.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'At ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Airhead Games',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ', we asked a simple question: ',
                        },
                        {
                            type: 'em',
                            children: [
                                {
                                    type: 'text',
                                    text: 'What if the deck of cards was infinite?',
                                },
                            ],
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Today, I am thrilled to announce the ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'General Availability (GA) launch of the new Airhead Games platform',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ". But this isn't just a software update. It is the beginning of a new era for community gaming—one where the host knows your name, the content is generated live, and the game never plays the same way twice.",
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'article__the-airhead-difference',
                    title: [
                        {
                            type: 'text',
                            text: 'The Airhead Difference',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Most games rely on static databases of questions written years ago. Airhead Games relies on Poppy, our dynamic host. Because our games are driven by real-time AI, Poppy doesn\'t just "host" the game; Poppy co-creates it with you. This allows for an experience that standard party games simply can\'t match.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "It is deeply personal: Poppy calls out players by name, riffs on your specific answers, and weaves your group's inside jokes into the fabric of the game. By generating new content every single turn, the games become infinitely replayable.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "Best of all, this dynamic approach allows the game to fit your specific group, not the other way around. Whether you want a family-friendly game night or an edgy late-night stream, Poppy can adjust personality and the game's content to match your vibe in seconds.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__setting-the-foundation-for-the-future',
                    title: [
                        {
                            type: 'text',
                            text: 'Setting the Foundation for the Future',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'To make this vision a reality, we needed a foundation strong enough to handle live AI generation, complex player interactions, and thousands of concurrent users. Over the last few months, we undertook ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: '"Project Big Brain,"',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' a complete re-architecture of our platform.',
                                },
                            ],
                        },
                        {
                            type: 'quote',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Our platform delivers a new kind of party game experience: deeply personal and always fresh.',
                                },
                            ],
                            attribution: [
                                {
                                    type: 'text',
                                    text: 'Jay, Developer',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'We moved from a browser-reliant model to a server-authoritative powerhouse. The result is a party game platform with less friction and more fun. The new platform delivers flawless sync between hosts and players, instant recovery if a player reconnects, and zero setup time. There are no downloads and no accounts required for players: just scan a QR code or click a link, and you are in the game.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "Overall, the new platform is more reliable, scalable, and better looking. It's loaded with new features and opens exciting possibilities for the future of our party games.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__flagship-release-meme-mash',
                    title: [
                        {
                            type: 'text',
                            text: 'Flagship Release: Meme Mash',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'To celebrate this launch, we are re-releasing the game that perfectly embodies this new philosophy: ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/games/meme-mash',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Meme Mash',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ". Meme Mash isn't just about filling in blanks; it's about creative collaboration between players and Poppy.",
                                },
                            ],
                        },
                        {
                            type: 'list',
                            items: [
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Choose',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ': A player picks from a list of generated meme templates.',
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Create:',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' Players fill in the blanks of the template with their own creative answers.',
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Remix:',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: " You steal other players' answers to rebuild the meme template with the funniest combination possible.",
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Reveal:',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' Answers used in the most remixes become the best meme and a unique image is generated based on the winning text.',
                                    },
                                ],
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'coverMemeMash',
                            imageAlt: 'Meme Mash cover art',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Meme Mash cover art — the game is available now.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Since the templates are generated fresh, player remixes produce wild memes, and the images are always unique, Meme Mash is the perfect embodiment of how Airhead Games can provide endless fun to different communities of players.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__a-platform-for-everyone',
                    title: [
                        {
                            type: 'text',
                            text: 'A Platform for Everyone',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'We built Airhead Games to be the ultimate connector. For our ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'content creators',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', this means robust moderation tools, royalty-free music, and unique interactive content that helps your stream stand out from the crowd. For ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'remote teams',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', it offers a way to energize meetings and break the ice without derailing the agenda. And for ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'friends',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', whether on the couch or oceans apart, it ensures that no one is left out of the fun.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__co-authored-by-community',
                    title: [
                        {
                            type: 'text',
                            text: 'Co-Authored by Community',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Poppy may be the voice of the platform, but the soul of Airhead Games belongs to the community. This platform was battle-tested by many Twitch streamers who embraced our early, messy, experimental builds.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'A special shoutout to ',
                                },
                                {
                                    type: 'link',
                                    href: 'https://twitch.tv/doctordiggitydank',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'DoctorDiggityDank',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', ',
                                },
                                {
                                    type: 'link',
                                    href: 'https://twitch.tv/edgywizard',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'EdgyWizard',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', and their respective communities, who saw the early potential of Airhead Games and most consistently helped to shape its future.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__endless-fun-available-now',
                    title: [
                        {
                            type: 'text',
                            text: 'Endless Fun, Available Now',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The new platform is live now with Meme Mash. The rest of the quick, multiplayer party games in our library will be migrated to the new platform over the coming weeks. Some new games will be added too!',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Grab your friends (or your chat) and get ready to experience a new era of party games. ',
                                },
                                {
                                    type: 'link',
                                    href: '/library',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Play Meme Mash Now',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        },
    ],
});
//# sourceMappingURL=airhead-games-officially-launches.js.map